'use client';

import { createMysteryRoomLocalRuntime, type MysteryRoomLocalRuntime } from '../core/localRuntime';
import { buildVisibilityState } from '../systems/visibilitySystem';
import type { MysteryRoomInventoryItemId, MysteryRoomMapDefinition } from '../world/mapTypes';
import type { MrMatchSnapshot } from './mysteryRoomApi';
import { mrIsNetworkMovementActive } from './interpolation';
import { mrPredictPathPosition } from './prediction';

type CreateClientSimulationMirrorInput = {
  snapshot: MrMatchSnapshot | null;
  selfPlayerId: string | null;
  serverNowMs: number;
  mapDefinition?: MysteryRoomMapDefinition;
  activeInventoryItemId?: string | null;
  flashlightEnabled?: boolean;
  previous?: MysteryRoomLocalRuntime | null;
};

export function createMysteryRoomClientSimulationMirror(
  input: CreateClientSimulationMirrorInput,
): MysteryRoomLocalRuntime | null {
  if (!input.snapshot) {
    return input.previous ?? null;
  }

  const mapDefinition = input.mapDefinition;
  const mapId = mapDefinition?.id ?? input.snapshot.mapId ?? 'lisad_9_night_shift';
  const previous = input.previous?.map.id === mapId ? input.previous : null;
  const base = previous ?? createMysteryRoomLocalRuntime(mapId, mapDefinition ? { mapDefinition } : {});
  const self = input.snapshot.players.find((player) => player.playerId === input.selfPlayerId) ?? input.snapshot.players[0];
  if (!self) {
    return base;
  }

  const position = self.movement?.state === 'moving'
    ? mrPredictPathPosition({
        x: self.x,
        y: self.y,
        targetX: self.movement.targetX,
        targetY: self.movement.targetY,
        startedAtMs: self.movement.startedAtMs,
        etaMs: self.movement.etaMs,
        nowMs: input.serverNowMs,
        path: self.movement.path,
      })
    : { x: self.x, y: self.y };
  const target = {
    x: self.movement?.targetX ?? position.x,
    y: self.movement?.targetY ?? position.y,
  };
  const movementActive = mrIsNetworkMovementActive({
    state: self.movement?.state,
    nowMs: input.serverNowMs,
    etaMs: self.movement?.etaMs,
    position,
    target,
  });
  const previousHeroPosition = previous?.hero.position ?? position;
  const velocity = {
    x: position.x - previousHeroPosition.x,
    y: position.y - previousHeroPosition.y,
  };
  const inventory = (self.inventory ?? []).map((item, index) => ({
    id: item.id as MysteryRoomInventoryItemId,
    label: item.label,
    kind: item.kind as MysteryRoomLocalRuntime['inventory'][number]['kind'],
    isWeapon: Boolean(item.isWeapon),
    acquiredAtMs: item.acquiredAtMs ?? input.serverNowMs,
    quantity: item.quantity ?? 1,
    slotIndex: item.slotIndex ?? index,
    icon: item.icon ?? item.label.slice(0, 2).toUpperCase(),
    iconAssetId: item.iconAssetId,
    iconAssetUrl: item.iconAssetUrl,
    iconAssetName: item.iconAssetName,
    iconAssetMimeType: item.iconAssetMimeType,
    description: item.description,
    sourceObjectId: item.sourceObjectId,
  }));
  const usedInteractableIds = new Set(input.snapshot.usedInteractableIds ?? []);
  const doorById = new Map((input.snapshot.doors ?? []).map((door) => [door.id, door]));
  const activeInventoryItemId =
    (input.activeInventoryItemId ?? self.activeInventoryItemId ?? null) as MysteryRoomInventoryItemId | null;

  const mirror: MysteryRoomLocalRuntime = {
    ...base,
    elapsedMs: input.serverNowMs,
    hero: {
      ...base.hero,
      position,
      velocity,
      zoneId: self.roomId ?? base.hero.zoneId,
      hidden: Boolean(input.snapshot.selfState?.isHidden),
      hideZoneId: input.snapshot.selfState?.hideZoneId ?? null,
      movementMode: movementActive ? 'auto' : 'idle',
      animationState: movementActive || Math.hypot(velocity.x, velocity.y) > 0.35 ? 'walk' : 'idle',
      speed: self.movement?.speed ?? base.hero.speed,
      health: self.health ?? base.hero.health,
      maxHealth: self.maxHealth ?? base.hero.maxHealth,
      alive: (self.health ?? 100) > 0,
    },
    input: {
      ...base.input,
      pointerTarget: movementActive ? target : null,
    },
    doors: base.doors.map((door) => {
      const state = doorById.get(door.id);
      const locked = state?.locked ?? false;
      const open = state?.open ?? false;
      return {
        ...door,
        state: locked ? 'locked' : open ? 'open' : 'closed',
        openProgress: !locked && open ? 1 : 0,
        holdOpenMs: open ? 900 : 0,
      };
    }),
    interactables: base.interactables.map((interactable) => ({
      ...interactable,
      state: usedInteractableIds.has(interactable.id) ? 'used' : 'idle',
    })),
    inventory,
    activeInventoryItemId,
    flashlightEnabled: Boolean(input.flashlightEnabled ?? self.flashlightEnabled),
    events: (input.snapshot.publicEvents ?? []).slice(-10).map((event) => {
      const entry = event as Record<string, unknown>;
      return String(entry.type ?? 'event');
    }),
  };

  return {
    ...mirror,
    visibility: buildVisibilityState(mirror),
  };
}
